<ng-container *ngIf="open">
  <div class="big-screen" [class.dark]="darkMode">
    <!-- Top bar for the picker/create screens only; the widget wall carries its
         own lean header so it doesn't waste vertical space. -->
    <div class="big-screen-topbar" *ngIf="view !== 'view'">
      <div class="big-screen-title">
        <span class="big-screen-title-icon" aria-hidden="true"></span>
        <span>Big Screen Dashboards</span>
      </div>
      <button type="button" class="big-screen-exit" (click)="close()" aria-label="Exit big screen dashboard">
        <span aria-hidden="true">&times;</span>
        <span class="big-screen-exit-label">Exit</span>
      </button>
    </div>

    <!-- ===================== Picker ===================== -->
    <div class="big-screen-body" *ngIf="view === 'picker'">
      <div class="big-screen-panel">
        <div class="big-screen-panel-head">
          <h2>{{ selectingForExport ? 'Select dashboards to export' : 'Choose a dashboard' }}</h2>

          <!-- Normal mode: create / import / start-export. Export mode swaps in a
               confirm/cancel pair so the header controls match the task. -->
          <div class="big-screen-panel-actions" *ngIf="!selectingForExport">
            <button type="button" class="big-screen-btn" (click)="showCreate()">
              + New dashboard
            </button>
            <label class="big-screen-btn big-screen-import-btn">
              Import
              <input type="file" accept="application/json,.json" hidden
                (change)="onImportFileSelected($event)" />
            </label>
            <button type="button" class="big-screen-btn" (click)="startExportMode()">
              Export
            </button>
          </div>
          <div class="big-screen-panel-actions" *ngIf="selectingForExport">
            <button type="button" class="big-screen-btn" (click)="exitExportMode()">
              Cancel
            </button>
            <button type="button" class="big-screen-btn primary"
              [disabled]="exportSelectionCount === 0" (click)="exportSelected()">
              Export {{ exportSelectionCount || '' }}
            </button>
          </div>
        </div>

        <p class="big-screen-import-msg" *ngIf="importMessage"
          [class.error]="importError">{{ importMessage }}</p>

        <ul class="big-screen-list">
          <li
            *ngFor="let dashboard of dashboards; trackBy: trackById"
            class="big-screen-list-item"
            [class.selectable]="selectingForExport"
            [class.selected]="selectingForExport && isExportSelected(dashboard)"
            (click)="selectingForExport ? toggleExportSelection(dashboard, $event) : openDashboard(dashboard)"
          >
            <input *ngIf="selectingForExport" type="checkbox" class="big-screen-list-check"
              [checked]="isExportSelected(dashboard)"
              (click)="toggleExportSelection(dashboard, $event)"
              [attr.aria-label]="'Select ' + dashboard.name + ' for export'" />
            <div class="big-screen-list-info">
              <span class="big-screen-list-name">{{ dashboard.name }}</span>
              <span class="big-screen-list-desc" *ngIf="dashboard.description">
                {{ dashboard.description }}
              </span>
            </div>
            <button
              *ngIf="!selectingForExport"
              type="button"
              class="big-screen-delete"
              (click)="deleteDashboard(dashboard, $event)"
              [attr.aria-label]="'Delete ' + dashboard.name"
            >
              &times;
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- ===================== Create ===================== -->
    <div class="big-screen-body" *ngIf="view === 'create'">
      <div class="big-screen-panel">
        <div class="big-screen-panel-head">
          <h2>{{ hasNoDashboards ? 'Create your first dashboard' : 'New dashboard' }}</h2>
        </div>
        <p class="big-screen-hint" *ngIf="hasNoDashboards">
          You don't have any dashboards yet. Give one a name to get started.
        </p>

        <label class="big-screen-field">
          <span class="big-screen-label">Name</span>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newName"
            (keyup.enter)="createDashboard()"
            placeholder="e.g. Main Gate Wall Display"
            autocomplete="off"
            maxlength="80"
          />
          <span class="big-screen-error" *ngIf="createAttempted && !canCreate">
            Please enter a name.
          </span>
        </label>

        <label class="big-screen-field">
          <span class="big-screen-label">Description <em>(optional)</em></span>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newDescription"
            placeholder="What this dashboard is for"
            autocomplete="off"
            maxlength="160"
          />
        </label>

        <p class="big-screen-error" *ngIf="saveError">{{ saveError }}</p>
        <p class="big-screen-import-msg" *ngIf="importMessage"
          [class.error]="importError">{{ importMessage }}</p>

        <div class="big-screen-form-actions">
          <button
            type="button"
            class="big-screen-btn"
            *ngIf="!hasNoDashboards"
            (click)="showPicker()"
          >
            Cancel
          </button>
          <!-- A first dashboard can also be seeded from an exported layout for this
               venue, so offer an import alongside creating one by hand. -->
          <label class="big-screen-btn big-screen-import-btn">
            Import a layout
            <input type="file" accept="application/json,.json" hidden
              (change)="onImportFileSelected($event)" />
          </label>
          <button type="button" class="big-screen-btn primary" (click)="createDashboard()">
            Create dashboard
          </button>
        </div>
      </div>
    </div>

    <!-- ===================== View (widget wall) ===================== -->
    <div class="big-screen-view" *ngIf="view === 'view' && activeDashboard"
      [class.fullscreen]="fullscreen"
      [style.background]="backgroundColor || null">
      <div class="big-screen-view-head" *ngIf="!fullscreen">
        <div class="big-screen-view-head-left">
          <h2>{{ activeDashboard.name }}</h2>
          <span class="big-screen-editing-badge" *ngIf="editing">Editing</span>
        </div>
        <div class="big-screen-view-head-right">
          <label class="big-screen-color-toggle" *ngIf="editing"
            title="Background colour" aria-label="Background colour"
            [style.background]="backgroundColor || null">
            <span class="material-symbols-outlined" aria-hidden="true">wallpaper</span>
            <input #bgInput type="color" class="big-screen-color-input"
              [value]="backgroundColor || (darkMode ? '#0f172a' : '#f4f6fa')"
              (input)="onBackgroundColorChange(bgInput.value)" />
          </label>
          <label class="big-screen-color-toggle" *ngIf="editing"
            title="Widget colour" aria-label="Widget colour"
            [style.background]="widgetColor || null">
            <span class="material-symbols-outlined" aria-hidden="true">widgets</span>
            <input #widgetInput type="color" class="big-screen-color-input"
              [value]="widgetColor || (darkMode ? '#1e293b' : '#ffffff')"
              (input)="onWidgetColorChange(widgetInput.value)" />
          </label>
          <button type="button" class="big-screen-view-link" (click)="showPicker()">
            All dashboards
          </button>
          <button type="button" class="big-screen-theme-toggle" (click)="requestToggleDarkMode()"
            [attr.aria-label]="darkMode ? 'Switch to light mode' : 'Switch to dark mode'">
            <span class="material-symbols-outlined" aria-hidden="true">
              {{ darkMode ? 'light_mode' : 'dark_mode' }}
            </span>
          </button>
          <button type="button" class="big-screen-exit" (click)="close()"
            aria-label="Exit big screen dashboard">
            <span aria-hidden="true">&times;</span>
            <span class="big-screen-exit-label">Exit</span>
          </button>
        </div>
      </div>

      <div class="big-screen-canvas" [class.editing]="editing">
        <!-- Ticketmaster branding, pinned to the wall's top-right corner. Shows in
             the normal, full-screen and edit views alike; pointer-events are off so
             it never blocks dragging a widget beneath it. -->
        <span class="big-screen-logo" role="img" aria-label="Ticketmaster"></span>

        <div class="big-screen-empty" *ngIf="!activeDashboard.widgets.length">
          <p class="big-screen-empty-text">
            {{ editing ? 'Click "Add widget" to start building your dashboard.' :
               'This dashboard has no widgets yet. Click Edit to add some.' }}
          </p>
          <!-- An empty dashboard can be seeded from an exported layout for this
               venue, so offer an import here as an alternative to building by hand. -->
          <label class="big-screen-btn big-screen-empty-import">
            Import a layout
            <input type="file" accept="application/json,.json" hidden
              (change)="onImportIntoActive($event)" />
          </label>
          <p class="big-screen-import-msg" *ngIf="importMessage"
            [class.error]="importError">{{ importMessage }}</p>
        </div>

        <app-widget-host *ngFor="let widget of activeDashboard.widgets; trackBy: trackByWidget"
          [widget]="widget"
          [editing]="editing"
          [dark]="widgetIsDark(widget)"
          [cardColor]="widgetColor"
          (layoutChange)="updateWidgetLayout(widget, $event)"
          (configure)="configureWidget(widget)"
          (remove)="removeWidget(widget)">
          <app-widget-content
            [widget]="widget"
            [attendanceSummary]="attendanceSummary"
            [entranceGroups]="entranceGroups"
            [timedGateEntries]="timedGateEntries"
            [timedScannerEntries]="timedScannerEntries"
            [chartColors]="chartColors"
            [editing]="editing"
            [dark]="widgetIsDark(widget)"
            (widgetChange)="onWidgetContentChanged($event)">
          </app-widget-content>
        </app-widget-host>
      </div>

      <!-- Floating action button: a speed-dial for edit-mode actions. Tapping the
           + circle fans out "Edit"/"Add widget"; the scrim behind it closes it. -->
      <div class="big-screen-fab" [class.open]="fabOpen">
        <div class="big-screen-fab-scrim" *ngIf="fabOpen" (click)="closeFab()"></div>

        <div class="big-screen-fab-actions" [class.open]="fabOpen">
          <button type="button" class="big-screen-fab-action" *ngIf="!editing"
            (click)="toggleEditing()">
            <span class="big-screen-fab-action-label">Edit</span>
            <span class="material-symbols-outlined big-screen-fab-action-icon" aria-hidden="true">
              edit
            </span>
          </button>
          <button type="button" class="big-screen-fab-action" (click)="openAddWidget()">
            <span class="big-screen-fab-action-label">Add widget</span>
            <span class="material-symbols-outlined big-screen-fab-action-icon" aria-hidden="true">
              add_2
            </span>
          </button>
          <button type="button" class="big-screen-fab-action" (click)="exportActive()">
            <span class="big-screen-fab-action-label">Export</span>
            <span class="material-symbols-outlined big-screen-fab-action-icon" aria-hidden="true">
              download
            </span>
          </button>
        </div>

        <div class="big-screen-fab-buttons">
          <button type="button" class="big-screen-fab-fullscreen" (click)="toggleFullscreen()"
            [attr.aria-label]="fullscreen ? 'Exit full screen' : 'Enter full screen'">
            <span class="material-symbols-outlined big-screen-fab-icon" aria-hidden="true">
              {{ fullscreen ? 'collapse_content' : 'expand_content' }}
            </span>
          </button>

          <button type="button" class="big-screen-fab-save" *ngIf="editing"
            (click)="toggleEditing()" aria-label="Save and finish editing">
            <span class="material-symbols-outlined big-screen-fab-icon" aria-hidden="true">save</span>
          </button>

          <button type="button" class="big-screen-fab-toggle" [class.open]="fabOpen"
            [class.editing]="editing" (click)="toggleFab()"
            [attr.aria-label]="fabOpen ? 'Close menu' : 'Open dashboard actions'"
            [attr.aria-expanded]="fabOpen">
            <span class="material-symbols-outlined big-screen-fab-icon" aria-hidden="true">add_2</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add-widget picker and per-widget config, layered above the overlay -->
  <app-add-widget
    [open]="addWidgetOpen"
    [dark]="darkMode"
    (add)="addWidget($event)"
    (cancel)="closeAddWidget()">
  </app-add-widget>

  <app-widget-config
    [widget]="configuringWidget"
    [isNew]="configuringIsNew"
    [attendanceSummary]="attendanceSummary"
    [entranceGroups]="entranceGroups"
    [timedGateEntries]="timedGateEntries"
    [timedScannerEntries]="timedScannerEntries"
    [dark]="darkMode"
    (save)="onWidgetConfigured($event)"
    (groupsChange)="onGroupsChanged($event)"
    (cancel)="closeConfigure()">
  </app-widget-config>

  <!-- Warns that switching light/dark will drop the dashboard's custom colours,
       since the new palette's defaults replace them. -->
  <div class="big-screen-confirm-backdrop" *ngIf="themeWarningOpen"
    [class.dark]="darkMode" (click)="cancelThemeSwitch()">
    <div class="big-screen-confirm" role="alertdialog" aria-modal="true"
      aria-labelledby="themeWarningTitle" aria-describedby="themeWarningBody"
      (click)="$event.stopPropagation()">
      <h3 id="themeWarningTitle" class="big-screen-confirm-title">
        Switch to {{ darkMode ? 'light' : 'dark' }} mode?
      </h3>
      <p id="themeWarningBody" class="big-screen-confirm-body">
        This dashboard uses custom colours. Switching the theme will override them
        with the {{ darkMode ? 'light' : 'dark' }} theme's default colours.
      </p>
      <div class="big-screen-confirm-actions">
        <button type="button" class="big-screen-btn" (click)="cancelThemeSwitch()">
          Cancel
        </button>
        <button type="button" class="big-screen-btn primary" (click)="confirmThemeSwitch()">
          Switch anyway
        </button>
      </div>
    </div>
  </div>
</ng-container>
